import { useParams, useSearchParams } from 'react-router-dom'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Button } from '@/components/ui/button'
import { ExternalLink, AlertTriangle, SearchX } from 'lucide-react'
import type { PublicReviewsPageData } from '@/types/PublicReviewPage.types'
import { LoadingSkeleton } from '@/components/ui/LoadingSkeleton'
import { PublicReviewPageSidebar } from '@/components/public-review/PublicReviewPage.Sidebar'
import { PublicReviewPageReviewsSection } from '@/components/public-review/PublicReviewPage.ReviewsSection'
import useDebounce from '@/hooks/useDebounce'
import { cn } from '@/lib/utils'
import { config } from '../../config'
import { SubmitReviewModal } from '@/components/public-review/SubmitReviewModal'
import { getApiBaseUrl } from '@/lib/api'
import { useTranslation } from '@/hooks/useTranslation'

const PublicReviewPage = () => {
	const { t } = useTranslation()
	const { program_identifier } = useParams<{ program_identifier: string }>()
	const [searchParams, setSearchParams] = useSearchParams()

	// currentPage is derived from URL, ensuring it's always in sync
	const currentPage = parseInt(searchParams.get('page') || '1', 10)

	// const [showWidget, setShowWidget] = useState(false) // Commented out old widget state
	const [isReviewModalOpen, setIsReviewModalOpen] = useState(false)
	const [isFilterOpen, setIsFilterOpen] = useState(false)

	// Local states for filters, initialized from URL or to defaults
	const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '')
	const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'newest')
	const [ratingFilter, setRatingFilter] = useState<number | null>(() => {
		const rating = searchParams.get('rating')
		return rating ? parseInt(rating, 10) : null
	})

	const debouncedSearchTerm = useDebounce(searchTerm, 500)

	// Effect to update URL search parameters when filter states change
	useEffect(() => {
		const newUrlParams = new URLSearchParams()
		// When filters change, always reset to page 1
		newUrlParams.set('page', '1')

		if (debouncedSearchTerm) {
			newUrlParams.set('search', debouncedSearchTerm)
		}
		// Store the UI-friendly sortBy value in the URL
		if (sortBy && sortBy !== 'newest') {
			newUrlParams.set('sortBy', sortBy)
		}
		if (ratingFilter !== null) {
			newUrlParams.set('rating', ratingFilter.toString())
		}

		// Only update if the generated params string (excluding page for this specific check)
		// is different from the current one, or if page is not 1.
		// This is to ensure that if filters change, URL is updated and page is reset.
		const currentParamsForFilterCheck = new URLSearchParams(
			searchParams.toString()
		)
		currentParamsForFilterCheck.delete('page')

		const newParamsForFilterCheck = new URLSearchParams(newUrlParams.toString())
		newParamsForFilterCheck.delete('page')

		if (
			currentParamsForFilterCheck.toString() !==
			newParamsForFilterCheck.toString()
		) {
			setSearchParams(newUrlParams, { replace: true })
		}
	}, [debouncedSearchTerm, sortBy, ratingFilter, setSearchParams, searchParams]) // searchParams is included to re-evaluate if URL was changed externally

	const { data, isLoading, error, refetch, isPlaceholderData } = useQuery<
		PublicReviewsPageData,
		Error
	>({
		// Query key depends on currentPage (from URL) and local filter states
		queryKey: [
			'publicProgramReviews',
			program_identifier,
			currentPage,
			debouncedSearchTerm,
			sortBy,
			ratingFilter,
		],
		queryFn: async () => {
			if (!program_identifier) throw new Error(t('publicReviewPage.programIdentifierMissing'))
			const params = new URLSearchParams({
				page: currentPage.toString(),
				limit: '10',
			})
			if (debouncedSearchTerm) params.append('search', debouncedSearchTerm)

			// Map UI sortBy to API sortBy inside queryFn
			let apiSortParam = 'created_at_desc' // Default sort for API
			if (sortBy === 'newest') {
				apiSortParam = 'created_at_desc'
			} else if (sortBy === 'oldest') {
				// Example: if 'oldest' was a UI option
				apiSortParam = 'created_at_asc'
			} else if (sortBy === 'highest') {
				apiSortParam = 'score_desc'
			} else if (sortBy === 'lowest') {
				apiSortParam = 'score_asc'
			}
			// Only append sortBy if it's not the default implied by the backend or if it's explicitly set
			if (sortBy !== 'newest' || apiSortParam !== 'created_at_desc') {
				// Add if not default API sort
				params.append('sortBy', apiSortParam)
			}

			if (ratingFilter !== null)
				params.append('rating', ratingFilter.toString())

			const response = await fetch(
				`${getApiBaseUrl()}/api/public/program-reviews/${program_identifier}?${params.toString()}`
			)

			if (!response.ok) {
				const errorData = await response.json().catch(() => ({}))
				throw new Error(
					errorData.error || t('publicReviewPage.failedToFetchReviews', { statusText: response.statusText })
				)
			}
			return response.json()
		},
		enabled: !!program_identifier,
		placeholderData: keepPreviousData,
	})

	const handlePageChange = (newPage: number) => {
		const newUrlParams = new URLSearchParams(searchParams.toString())
		newUrlParams.set('page', newPage.toString())
		setSearchParams(newUrlParams)
		window.scrollTo(0, 0)
	}

	const handleWriteReview = () => {
		if (data?.program.allow_new_submissions) {
			// setShowWidget(true) // Commented out old logic
			setIsReviewModalOpen(true) // Open the new modal
		} else {
			console.warn(
				t('publicReviewPage.reviewSubmissionsNotEnabled')
			)
		}
	}

	// Render loading, error, or no data states
	if (isLoading && !isPlaceholderData && !data) {
		// Show full skeleton only on initial load without placeholder
		return (
			<div className="min-h-screen w-full bg-gray-50 dark:bg-gray-950 flex flex-col">
				<header className="w-full py-4 px-4 md:px-8 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
					<div className="max-w-7xl mx-auto flex justify-between items-center">
						<div className="flex items-center gap-2">
							<div className="h-10 w-10 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
							<div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
						</div>
						<div className="h-10 w-28 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
					</div>
				</header>
				<LoadingSkeleton />
				<footer className="mt-auto py-6 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
					<div className="max-w-7xl mx-auto px-4 md:px-8">
						<div className="h-4 w-48 bg-gray-200 dark:bg-gray-700 rounded mx-auto"></div>
					</div>
				</footer>
			</div>
		)
	}

	if (error) {
		return (
			<div className="min-h-screen w-full flex items-center justify-center p-4 text-center bg-red-50 dark:bg-red-900/20">
				<div>
					<AlertTriangle className="mx-auto h-12 w-12 text-red-500 dark:text-red-400 mb-4" />
					<h1 className="text-2xl font-semibold text-red-700 dark:text-red-300 mb-2">
						{t('publicReviewPage.errorLoadingReviews')}
					</h1>
					<p className="text-red-600 dark:text-red-400">{error.message}</p>
					<Button variant="outline" onClick={() => refetch()} className="mt-6">
						{t('publicReviewPage.tryAgain')}
					</Button>
				</div>
			</div>
		)
	}

	if (!data && !isLoading) {
		// Handle case where data is null after loading (e.g. program not found)
		return (
			<div className="min-h-screen w-full flex items-center justify-center p-4 text-center bg-gray-50 dark:bg-gray-900">
				<div>
					<SearchX className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" />
					<h1 className="text-2xl font-semibold text-gray-700 dark:text-gray-200 mb-2">
						{t('publicReviewPage.noReviewDataAvailable')}
					</h1>
					<p className="text-gray-500 dark:text-gray-400">
						{t('publicReviewPage.couldNotLoadReviewInformation')}
					</p>
				</div>
			</div>
		)
	}

	// If data exists (even if it's placeholder data), proceed to render
	const program = data!.program
	const statistics = data!.statistics
	const reviews = data!.reviews
	const pagination = data!.pagination

	return (
		<div
			className={cn(
				'min-h-screen w-full bg-gray-50 dark:bg-gray-950 text-gray-800 dark:text-gray-100'
			)}>
			<Helmet>
				<title>{`${program.name} ${t('publicReviewPage.reviews')}${program.page_title_suffix ? ` - ${program.page_title_suffix}` : ''} | Feedbask`}</title>
				<meta
					name="description"
					content={
						program.meta_description ||
						t('publicReviewPage.seoDescription', { programName: program.name })
					}
				/>
			</Helmet>

			{/* <header className="w-full py-4 px-4 md:px-8 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
				<div className="max-w-7xl mx-auto flex flex-row justify-between items-center gap-4">
					<div className="flex items-center gap-3">
						<img
							src={config.logo}
							alt="Feedbask Logo"
							className="w-full h-12"
						/>
					</div>
				</div>
			</header> */}

			<section className=" mx-auto md:px-0 bg-white border-b border-gray-200 dark:border-gray-700">
				<div className=" dark:bg-gray-800 max-w-7xl mx-auto rounded-lg p-6 flex flex-col md:flex-row items-start md:items-center gap-6">
					{program.logo_url && (
						<img
							src={program.logo_url}
							alt={`${program.name} Logo`}
							className="h-24 w-24 object-contain rounded-md border border-gray-200 dark:border-gray-700 p-1 bg-gray-50 dark:bg-gray-750 shrink-0"
						/>
					)}
					{!program.logo_url && (
						<div className="h-24 w-24 flex items-center justify-center bg-gray-200 dark:bg-gray-700 rounded-md text-gray-500 dark:text-gray-400 shrink-0">
							{t('publicReviewPage.logoPlaceholder')}
						</div>
					)}
					<div className="flex-1">
						<h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-50 mb-1">
							{program.name}
						</h1>
						<p className="text-gray-600 dark:text-gray-400 text-sm mb-2">
							{program.description || t('publicReviewPage.noDescriptionAvailable')}
						</p>
						{statistics && statistics.total_reviews > 0 && (
							<div className="flex items-center gap-2 mb-3">
								<div className="flex items-center">
									{[...Array(5)].map((_, i) => (
										<svg
											key={i}
											className={cn(
												'w-5 h-5',
												i < Math.round(statistics.average_rating)
													? 'text-yellow-400'
													: 'text-gray-300 dark:text-gray-600'
											)}
											fill="currentColor"
											viewBox="0 0 20 20">
											<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.368 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.368-2.448a1 1 0 00-1.176 0l-3.368 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.073 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69L9.049 2.927z" />
										</svg>
									))}
								</div>
								<span className="text-sm text-gray-500 dark:text-gray-400">
									{t('publicReviewPage.basedOnReviews', { count: statistics.total_reviews })}
								</span>
							</div>
						)}
						{statistics && statistics.total_reviews === 0 && (
							<p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
								{t('publicReviewPage.noReviewsYet')}
							</p>
						)}
					</div>
					{program.website_url && (
						<Button
							asChild
							variant="default"
							size="sm"
							className="text-white shrink-0 md:ml-auto mt-4 md:mt-0">
							<a
								href={program.website_url}
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center gap-1.5">
								{t('publicReviewPage.visitWebsite')}
								<ExternalLink size={16} />
							</a>
						</Button>
					)}
				</div>
			</section>

			<main className="max-w-7xl mx-auto px-4 md:px-8 py-6">
				<div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
					<PublicReviewPageSidebar
						program={program}
						statistics={statistics}
						handleWriteReview={handleWriteReview}
						isLoading={isLoading && isPlaceholderData} // Pass refined loading state for sidebar skeleton
						isPlaceholderData={isPlaceholderData}
					/>

					<PublicReviewPageReviewsSection
						programName={program.name}
						allowNewSubmissions={program.allow_new_submissions}
						widgetIdForSubmissions={program.widget_id_for_submissions}
						reviews={reviews}
						pagination={pagination}
						handlePageChange={handlePageChange}
						handleWriteReview={handleWriteReview}
						showContentLoading={isLoading && isPlaceholderData} // Pass refined loading state
						isLoading={isLoading}
						isPlaceholderData={isPlaceholderData}
						searchTerm={searchTerm}
						setSearchTerm={setSearchTerm}
						isFilterOpen={isFilterOpen}
						setIsFilterOpen={setIsFilterOpen}
						ratingFilter={ratingFilter}
						setRatingFilter={setRatingFilter}
						sortBy={sortBy}
						setSortBy={setSortBy}
					/>
				</div>
			</main>

			{
				<footer className="mt-8 py-6 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
					<div className="max-w-7xl mx-auto px-4 md:px-8">
						<div className="flex flex-col md:flex-row justify-center items-center">
							<p className="text-gray-500 dark:text-gray-400 text-sm flex items-center gap-2">
								{t('publicReviewPage.poweredBy')}{' '}
								<a
									href="https://feedbask.com"
									target="_blank"
									rel="noopener noreferrer"
									className="font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300">
									<img
										src={config.logo}
										alt="Feedbask Logo"
										className="w-full h-10"
									/>
								</a>
							</p>
						</div>
					</div>
				</footer>
			}

			{/* New Review Submission Modal */}
			{program_identifier && (
				<SubmitReviewModal
					isOpen={isReviewModalOpen}
					onClose={() => setIsReviewModalOpen(false)}
					programId={data.program.program_id} // program_identifier is the ID
					widgetId={data.program.widget_id_for_submissions} // This is used as widgetId by the API
					programName={program.name}
				/>
			)}
		</div>
	)
}

export default PublicReviewPage
